/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useNavigate } from '@tanstack/react-router';
import { Tooltip } from 'radix-ui';
import React, { useEffect, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';

import { useTranslation } from '~/app';
import { localeCookieName, localeLocalStorageKey, STORAGE_KEY_ALIASES } from '~/app/storageKeys';
import { useAuthState } from '~/auth';
import ClientOnly from '~/components/ClientOnly';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/DropdownMenu/DropdownMenu';
import { cn } from '~/utils/styles';

// Shared button styling for placeholder and Radix trigger (avoids layout shift on hydration)
const languageButtonClassName =
  'transform-gpu hover:brightness-125 bg-primary-light active:brightness-125 my-1 flex h-10 min-w-0 max-w-full flex-row items-center justify-center gap-0 rounded-md p-2 px-3 transition-all 2xl:max-w-[min(100%,14rem)] 2xl:justify-start cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2';

// Language icon component (used by placeholder and Radix button)
const LanguageIcon = () => (
  <svg
    className="h-5 w-5 shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
    />
  </svg>
);

// Server-safe placeholder: same look and a11y, no Radix (avoids useId hydration mismatch)
const LanguageButtonPlaceholder = (): JSX.Element => {
  const { t } = useTranslation();
  const label = t('start_languageSelectionHeader');
  return (
    <button type="button" className={languageButtonClassName} aria-label={label} title={label}>
      <LanguageIcon />
      <span className="hidden min-w-0 flex-1 truncate pl-2 text-left text-sm leading-none 2xl:block">
        {label}
      </span>
    </button>
  );
};

// Get localized language name using browser's Intl API
const getLocalizedLanguageName = (languageCode: string, currentLocale: string): string => {
  try {
    // Use Intl.DisplayNames to get the language name in the current locale
    const displayNames = new Intl.DisplayNames([currentLocale], { type: 'language' });
    return displayNames.of(languageCode) || languageCode.toUpperCase();
  } catch {
    // Fallback to uppercase language code if Intl.DisplayNames is not supported
    return languageCode.toUpperCase();
  }
};

// Check icon component
const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    className={cn('h-4 w-4', className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const LanguageButton = (): JSX.Element | null => {
  const navigate = useNavigate();
  const { t, allLocales, urlPath, locale } = useTranslation();
  const { isAuthorized } = useAuthState();
  const [, setLocale] = useLocalStorageState(localeLocalStorageKey, { defaultValue: 'de' });
  const [isOpen, setIsOpen] = useState(false);

  // Persist locale to cookie when it changes (avoids modifying document in event handler)
  useEffect(() => {
    try {
      const maxAge = 60 * 60 * 24 * 365;
      document.cookie = `${localeCookieName}=${locale}; path=/; max-age=${maxAge}`;
      document.cookie = `${STORAGE_KEY_ALIASES.localeCookie[0]}=${locale}; path=/; max-age=${maxAge}`;
    } catch {
      /* ignore cookie errors (e.g. private mode) */
    }
  }, [locale]);

  // Only show when user is not logged in
  if (isAuthorized) {
    return null;
  }

  const handleLanguageSelect = (pLocale: string) => {
    setLocale(pLocale);
    // Since URLs are English-only, keep path and refresh translations
    navigate({ to: urlPath, replace: true });
    setIsOpen(false);
  };

  return (
    <ClientOnly fallback={<LanguageButtonPlaceholder />}>
      <Tooltip.Provider>
        <Tooltip.Root delayDuration={0}>
          <Tooltip.Trigger asChild>
            <div className="relative transform-gpu transition-all hover:scale-[1.02] active:scale-[0.98]">
              <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    className={languageButtonClassName}
                    aria-label={t('start_languageSelectionHeader')}
                    title={t('start_languageSelectionHeader')}
                  >
                    <LanguageIcon />
                    <span className="hidden min-w-0 flex-1 truncate pl-2 text-left text-sm leading-none 2xl:block">
                      {t('start_languageSelectionHeader')}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-xl sm:min-w-[240px] sm:p-2"
                >
                  {allLocales.map(({ locale: pLocale, country: pCountry }) => (
                    <DropdownMenuItem
                      key={pLocale}
                      onClick={() => handleLanguageSelect(pLocale)}
                      className={cn(
                        'flex w-full cursor-pointer flex-row items-center justify-start space-x-4 rounded-md px-3 py-2 text-left font-sans text-base font-medium transition-colors hover:bg-primary-light/20 focus:bg-primary-light/20 focus:outline-hidden sm:px-4 sm:py-3 sm:text-lg',
                        pLocale === locale && 'bg-primary-light/20',
                      )}
                    >
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-xs font-bold">
                        {pCountry}
                      </span>
                      <span className="flex flex-1 items-center">
                        {getLocalizedLanguageName(pLocale, locale)}
                      </span>
                      {pLocale === locale && <CheckIcon className="ml-auto h-4 w-4" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              sideOffset={8}
              className="rounded-lg border border-gray-100 bg-white px-2 py-2 text-sm shadow-xl"
            >
              {t('start_languageSelectionHeader')}
              <Tooltip.Arrow className="fill-white" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    </ClientOnly>
  );
};

export default LanguageButton;
